/**
 * Cubby operations, for platform staff only. The Kanvo /admin/health board,
 * plus feedback triage and the support inbox.
 */

import { useState } from "react";
import { useMe } from "../auth/MeProvider";
import { useSession } from "../auth/SessionProvider";
import { Button, Card, Empty, Notice, PageHeader, Tag, selectClass, textareaClass } from "../components/ui";
import { describeApiError } from "../lib/api";
import { formatDateTime } from "../lib/format";
import { useLoad } from "../lib/useLoad";

interface Health {
  events: { id: string; source: string; key: string; severity: string; message: string; count: number; lastSeenAt: string }[];
  heartbeats: { key: string; label: string; lastSuccessAt: string | null; lastStatus: string; lastDetail: string | null; stale: boolean }[];
  deadLetters: { id: string; provider: string; eventType: string | null; error: string; createdAt: string }[];
}

interface FeedbackItem {
  id: string;
  from_name: string | null;
  from_email: string | null;
  feedback_type: string;
  title: string;
  description: string;
  workflow_impact: string | null;
  status: string;
  priority: string | null;
  created_at: string;
}

interface TicketItem {
  id: string;
  subject: string;
  requester_email: string | null;
  ticket_type: string;
  status: string;
  last_message_at: string;
}

type Tab = "health" | "feedback" | "tickets";

const SEVERITY_TONE: Record<string, "neutral" | "warn" | "bad"> = {
  info: "neutral",
  warning: "warn",
  error: "bad",
  critical: "bad",
};

export function Ops() {
  const [tab, setTab] = useState<Tab>("health");
  const { me } = useMe();
  const readOnly = me.platformRole === "readonly" || me.platformRole === "billing";

  return (
    <div>
      <PageHeader title="Operations" detail={readOnly ? "Your support role can look but not change anything." : undefined} />
      <div className="mb-6 flex gap-2">
        {(["health", "feedback", "tickets"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-xl px-4 py-2 text-sm font-medium capitalize ${tab === t ? "bg-leaf-700 text-white" : "border border-stone-300 bg-white text-stone-700 hover:bg-stone-50"}`}
          >
            {t}
          </button>
        ))}
      </div>
      {tab === "health" ? <HealthTab readOnly={readOnly} /> : tab === "feedback" ? <FeedbackTab readOnly={readOnly} /> : <TicketsTab readOnly={readOnly} />}
    </div>
  );
}

function HealthTab({ readOnly }: { readOnly: boolean }) {
  const { api } = useSession();
  const { data, error, reload } = useLoad(() => api<Health>("/api/ops/health"), [api]);
  const [actionError, setActionError] = useState<string | null>(null);

  async function resolve(id: string) {
    const note = window.prompt("What fixed it?");
    if (!note) return;
    setActionError(null);
    try {
      await api(`/api/ops/events/${id}/resolve`, { method: "POST", body: JSON.stringify({ note }) });
      reload();
    } catch (caught) {
      setActionError(describeApiError(caught));
    }
  }

  return (
    <div className="space-y-6">
      {error ? <Notice>{error}</Notice> : null}
      {actionError ? <Notice>{actionError}</Notice> : null}
      <Card>
        <h2 className="text-lg font-semibold">Open events</h2>
        <div className="mt-4 space-y-2">
          {data?.events.length === 0 ? <Empty>Nothing open.</Empty> : null}
          {data?.events.map((e) => (
            <div key={e.id} className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-stone-200 px-4 py-3">
              <div className="min-w-0">
                <p className="font-medium">
                  <Tag tone={SEVERITY_TONE[e.severity] ?? "neutral"}>{e.severity}</Tag> {e.message}
                </p>
                <p className="mt-1 text-sm text-stone-500">
                  {e.source} / {e.key}, seen {e.count} {e.count === 1 ? "time" : "times"}, last {formatDateTime(e.lastSeenAt)}
                </p>
              </div>
              {!readOnly ? (
                <Button variant="secondary" size="sm" onClick={() => void resolve(e.id)}>
                  Resolve
                </Button>
              ) : null}
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-semibold">Scheduled jobs</h2>
        <div className="mt-4 space-y-2">
          {data?.heartbeats.length === 0 ? <Empty>No job has reported in yet. The scheduler registers itself on its first run.</Empty> : null}
          {data?.heartbeats.map((h) => (
            <div key={h.key} className="rounded-xl border border-stone-200 px-4 py-3">
              <p className="font-medium">
                {h.label} {h.stale ? <Tag tone="bad">Stale</Tag> : <Tag tone="ok">OK</Tag>}
              </p>
              <p className="mt-1 text-sm text-stone-500">
                Last success {h.lastSuccessAt ? formatDateTime(h.lastSuccessAt) : "never"}
                {h.lastDetail ? `. ${h.lastDetail}` : ""}
              </p>
            </div>
          ))}
        </div>
      </Card>
      <Card>
        <h2 className="text-lg font-semibold">Failed webhooks</h2>
        <div className="mt-4 space-y-2">
          {data?.deadLetters.length === 0 ? <Empty>None waiting.</Empty> : null}
          {data?.deadLetters.map((d) => (
            <div key={d.id} className="rounded-xl border border-stone-200 px-4 py-3 text-sm">
              <p className="font-medium">
                {d.provider} {d.eventType ?? ""}
              </p>
              <p className="mt-1 text-stone-500">
                {d.error}, {formatDateTime(d.createdAt)}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function FeedbackTab({ readOnly }: { readOnly: boolean }) {
  const { api } = useSession();
  const [status, setStatus] = useState("new");
  const { data, error, reload } = useLoad(
    () => api<{ feedback: FeedbackItem[] }>(`/api/ops/feedback${status ? `?status=${status}` : ""}`),
    [api, status],
  );

  async function update(id: string, patch: Record<string, unknown>) {
    await api(`/api/ops/feedback/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
    reload();
  }

  return (
    <Card>
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Feedback</h2>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectClass}>
          <option value="">All</option>
          {["new", "reviewing", "planned", "in_progress", "done", "declined"].map((s) => (
            <option key={s} value={s}>
              {s.replace("_", " ")}
            </option>
          ))}
        </select>
      </div>
      {error ? <div className="mt-4"><Notice>{error}</Notice></div> : null}
      <div className="mt-4 space-y-3">
        {data?.feedback.length === 0 ? <Empty>Nothing here.</Empty> : null}
        {data?.feedback.map((f) => (
          <div key={f.id} className="rounded-xl border border-stone-200 px-4 py-3">
            <p className="font-medium">
              <Tag tone={f.feedback_type === "bug_report" ? "bad" : "neutral"}>{f.feedback_type === "bug_report" ? "Bug" : "Request"}</Tag>{" "}
              {f.title}
              {f.workflow_impact === "blocking" ? <> <Tag tone="warn">Blocking</Tag></> : null}
            </p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-stone-700">{f.description}</p>
            <p className="mt-2 text-sm text-stone-500">
              {f.from_name ?? f.from_email ?? "Signed-in user"}, {formatDateTime(f.created_at)}
            </p>
            {!readOnly ? (
              <div className="mt-3 flex flex-wrap gap-2">
                <select value={f.status} onChange={(e) => void update(f.id, { status: e.target.value })} className={selectClass}>
                  {["new", "reviewing", "planned", "in_progress", "done", "declined"].map((s) => (
                    <option key={s} value={s}>
                      {s.replace("_", " ")}
                    </option>
                  ))}
                </select>
                <select value={f.priority ?? ""} onChange={(e) => void update(f.id, { priority: e.target.value || null })} className={selectClass}>
                  <option value="">No priority</option>
                  {["low", "medium", "high", "urgent"].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

function TicketsTab({ readOnly }: { readOnly: boolean }) {
  const { api } = useSession();
  const [status, setStatus] = useState("open");
  const [openId, setOpenId] = useState<string | null>(null);
  const list = useLoad(
    () => api<{ tickets: TicketItem[] }>(`/api/ops/tickets${status ? `?status=${status}` : ""}`),
    [api, status],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-5">
      <Card className="lg:col-span-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold">Tickets</h2>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectClass}>
            <option value="">All</option>
            {["open", "pending", "resolved", "closed"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
        {list.error ? <div className="mt-4"><Notice>{list.error}</Notice></div> : null}
        <div className="mt-4 space-y-2">
          {list.data?.tickets.length === 0 ? <Empty>No tickets.</Empty> : null}
          {list.data?.tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setOpenId(t.id)}
              className={`block w-full rounded-xl border px-4 py-3 text-left ${openId === t.id ? "border-leaf-600 bg-leaf-50" : "border-stone-200 hover:bg-stone-50"}`}
            >
              <span className="block font-medium">{t.subject}</span>
              <span className="mt-1 block text-sm text-stone-500">
                {t.requester_email ?? "No email"}, {t.ticket_type}, {formatDateTime(t.last_message_at)}
              </span>
            </button>
          ))}
        </div>
      </Card>
      <div className="lg:col-span-3">
        {openId ? <OpsTicket id={openId} readOnly={readOnly} onChange={list.reload} /> : <Empty>Pick a ticket.</Empty>}
      </div>
    </div>
  );
}

function OpsTicket({ id, readOnly, onChange }: { id: string; readOnly: boolean; onChange: () => void }) {
  const { api } = useSession();
  const { data, error, reload } = useLoad(
    () =>
      api<{
        ticket: { subject: string; status: string };
        messages: { id: string; author_kind: string; body: string; created_at: string }[];
        notes: { id: string; body: string; created_at: string }[];
      }>(`/api/ops/tickets/${id}`),
    [api, id],
  );
  const [reply, setReply] = useState("");
  const [asNote, setAsNote] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function send() {
    setActionError(null);
    try {
      await api(`/api/ops/tickets/${id}/${asNote ? "note" : "reply"}`, { method: "POST", body: JSON.stringify({ body: reply }) });
      setReply("");
      reload();
      onChange();
    } catch (caught) {
      setActionError(describeApiError(caught));
    }
  }

  async function setStatus(status: string) {
    await api(`/api/ops/tickets/${id}`, { method: "PATCH", body: JSON.stringify({ status }) });
    reload();
    onChange();
  }

  if (error) return <Notice>{error}</Notice>;
  if (!data) return null;

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">{data.ticket.subject}</h2>
        {!readOnly ? (
          <select value={data.ticket.status} onChange={(e) => void setStatus(e.target.value)} className={selectClass}>
            {["open", "pending", "resolved", "closed"].map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <div className="mt-4 space-y-3">
        {data.messages.map((m) => (
          <div key={m.id} className={`rounded-xl px-4 py-3 ${m.author_kind === "staff" ? "bg-leaf-50" : "border border-stone-200"}`}>
            <p className="text-sm text-stone-500">
              {m.author_kind === "staff" ? "Support" : "Customer"}, {formatDateTime(m.created_at)}
            </p>
            <p className="mt-1 whitespace-pre-wrap">{m.body}</p>
          </div>
        ))}
        {data.notes.map((n) => (
          <div key={n.id} className="rounded-xl bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800">Internal note, {formatDateTime(n.created_at)}</p>
            <p className="mt-1 whitespace-pre-wrap">{n.body}</p>
          </div>
        ))}
      </div>
      {!readOnly ? (
        <div className="mt-4 space-y-3">
          {actionError ? <Notice>{actionError}</Notice> : null}
          <textarea rows={4} value={reply} onChange={(e) => setReply(e.target.value)} placeholder={asNote ? "Internal note, never shown to the customer" : "Reply to the customer"} className={textareaClass} />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={asNote} onChange={(e) => setAsNote(e.target.checked)} className="h-4 w-4 accent-leaf-700" />
              Internal note
            </label>
            <Button disabled={!reply.trim()} onClick={() => void send()}>
              {asNote ? "Save note" : "Send reply"}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
